<template>
  <div class="time-grouped-list">
    <template v-for="(group, index) in groupedItems" :key="index">
      <UiTimeGroupSeparator :label="group.label" />
      <div class="group-items">
        <component 
          v-for="item in group.items" 
          :key="item.key" 
          :is="item.component" 
          v-bind="item.props" 
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import type { VNode } from 'vue'

const { $dayjs } = useNuxtApp() as any
const slots = useSlots()

interface GroupedItem {
  component: any
  props: any
  key: string | number
  addDate: Date
}

interface TimeGroup {
  label: string
  items: GroupedItem[]
  sortOrder: number
}

// Функция для определения временной группы
const getTimeGroup = (date: Date): { label: string, sortOrder: number } => {
  const now = new Date()
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterdayStart = new Date(dayStart.getTime() - 24 * 60 * 60 * 1000)
  const weekStart = new Date(dayStart.getTime() - 7 * 24 * 60 * 60 * 1000)
  const monthStart = new Date(dayStart.getTime() - 30 * 24 * 60 * 60 * 1000)
  const threeMonthsStart = new Date(dayStart.getTime() - 90 * 24 * 60 * 60 * 1000)
  
  if (date >= dayStart) {
    return { label: 'Today', sortOrder: 0 }
  } else if (date >= yesterdayStart) {
    return { label: 'Yesterday', sortOrder: 1 }
  } else if (date >= weekStart) {
    return { label: 'This week', sortOrder: 2 }
  } else if (date >= monthStart) {
    return { label: 'This month', sortOrder: 3 }
  } else if (date >= threeMonthsStart) {
    return { label: '3 months ago', sortOrder: 4 }
  } else {
    return { label: 'Earlier', sortOrder: 5 }
  }
}

// Извлекаем элементы из слота и группируем их
const groupedItems = computed<TimeGroup[]>(() => {
  const defaultSlot = slots.default?.()
  if (!defaultSlot) return []
  
  // Извлекаем все элементы с их add_date
  const items: GroupedItem[] = []
  
  const processVNodes = (vnodes: VNode[]) => {
    vnodes.forEach((vnode, index) => {
      if (vnode.type === 'template' || vnode.type === Symbol.for('v-fgt')) {
        // Обрабатываем фрагменты
        if (Array.isArray(vnode.children)) {
          processVNodes(vnode.children as VNode[])
        }
      } else if (vnode.props && typeof vnode.props === 'object') {
        // Ищем add_date в пропсах
        let addDate: string | null = null
        
        // Проверяем различные возможные места где может быть дата
        if (vnode.props.notification?.add_date) {
          addDate = vnode.props.notification.add_date
        } else if (vnode.props.group?.add_date) {
          addDate = vnode.props.group.add_date
        } else if (vnode.props.addDate) {
          addDate = vnode.props.addDate
        } else if (vnode.props['add-date']) {
          addDate = vnode.props['add-date']
        }
        
        if (addDate) {
          const parsedDate = $dayjs(addDate).toDate()
          items.push({
            component: vnode,
            props: vnode.props,
            key: (typeof vnode.key === 'string' || typeof vnode.key === 'number') ? vnode.key : `item-${index}`,
            addDate: parsedDate
          })
        }
      }
    })
  }
  
  processVNodes(defaultSlot)
  
  // Сортируем элементы по дате (новые сверху)
  items.sort((a, b) => b.addDate.getTime() - a.addDate.getTime())
  
  // Группируем по временным периодам
  const groups = new Map<string, TimeGroup>()
  
  items.forEach(item => {
    const timeGroup = getTimeGroup(item.addDate)
    const key = `${timeGroup.sortOrder}-${timeGroup.label}`
    
    if (!groups.has(key)) {
      groups.set(key, {
        label: timeGroup.label,
        items: [],
        sortOrder: timeGroup.sortOrder
      })
    }
    
    groups.get(key)!.items.push(item)
  })
  
  // Возвращаем группы отсортированные по sortOrder
  return Array.from(groups.values()).sort((a, b) => a.sortOrder - b.sortOrder)
})
</script>

<style scoped>
.time-grouped-list {
  display: flex;
  flex-direction: column;
}

.group-items {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
</style>
